<template>
  <header class="relative">
    <div
      class="border-b border-gray-200 bg-white fixed inset-0 z-20 flex flex-shrink-0 items-center justify-between h-16 lg:relative lg:h-auto lg:px-8 lg:z-20"
    >
      <!-- Mobile nav - Sidebar menu toggle, and SparkLayer icon -->
      <nav class="flex h-full lg:hidden">
        <button
          type="button"
          class="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500"
          @click="() => $emit('toggleSidebar')"
        >
          <span class="sr-only">Open sidebar</span>
          <MenuAlt1Icon class="h-6 w-6" />
        </button>

        <a
          href="/public"
          class="px-5 border-r flex items-center border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500"
        >
          <img
            class="h-6 w-auto"
            src="../../assets/logo-icon.png"
            alt="SparkLayer"
          >
        </a>
      </nav>

      <!-- Desktop title -->
      <div class="flex-1 min-w-0 hidden lg:block">
        <h1 class="text-lg font-medium leading-6 text-gray-900 sm:truncate">
          {{ title }}
        </h1>
      </div>

      <!-- Right side of header - Plan badge, environment badge, profile dropdown-->
      <div class="ml-4 flex items-center md:ml-6 flex relative z-10">
        <!-- Plan badge -->
        <div v-if="showPlanLink" class="hidden md:block">
          <router-link
            to="/settings/plan"
            class="inline-flex items-center mr-4 px-3 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-700"
          >
            {{ planLinkText }}
            <button
              type="button"
              class="flex-shrink-0 ml-0.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-purple-400 hover:bg-purple-200 hover:text-purple-500 focus:outline-none focus:bg-purple-500 focus:text-white"
            >
              <ArrowRightIcon class="h-4 w-4" />
            </button>
          </router-link>
        </div>

        <!-- Environment badge -->
        <div v-if="(!isPartner && isAdvancedFeaturesEnabled) || (isPartner && showCoreNav)">
          <div v-if="!isLive">
            <span class="relative inline-flex rounded-md shadow-sm">
              <span
                class="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800"
              >
                <GlobeAltIcon class="-ml-0.5 mr-1 h-4 w-4 text-yellow-700" />
                <span>Test Data</span>
              </span>
              <span class="flex absolute h-3 w-3 top-0 right-0 -mt-1 -mr-1">
                <span
                  class="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400/75"
                />
                <span class="relative inline-flex rounded-full h-3 w-3 bg-yellow-500" />
              </span>
            </span>
          </div>
          <div v-else>
            <span
              class="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800"
            >
              <CheckCircleIcon class="-ml-0.5 mr-1 h-4 w-4 text-green-700" />
              <span>Live Data</span>
            </span>
          </div>
        </div>

        <!-- Profile dropdown -->
        <div class="ml-3 relative pr-4 lg:pr-0">
          <div>
            <button
              type="button"
              class="max-w-xs bg-white rounded-full flex items-center text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-spark-purple lg:p-2 lg:my-4 lg:rounded-md lg:hover:bg-gray-50"
              aria-haspopup="true"
              @click="userMenuOpen = !userMenuOpen"
            >
              <div
                class="rounded-full h-8 w-8 flex items-center justify-center bg-spark-purple text-white font-medium text-xs tracking-widest"
              >
                <span class="w-5 overflow-hidden inline-block">
                  {{ acronymName }}
                </span>
              </div>
              <span
                class="hidden ml-3 text-gray-700 text-sm font-medium truncate text-ellipsis min-w-0 max-w-xs md:block"
              ><span
                class="sr-only"
              >Open user menu for </span>{{ name }}</span>
              <ChevronDownIcon class="hidden flex-shrink-0 ml-1 h-5 w-5 text-gray-400 md:block" />
            </button>
          </div>
          <div
            :class="{ hidden: !userMenuOpen }"
            class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black/5 divide-y divide-gray-200"
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="user-menu"
          >
            <div class="py-1">
              <router-link
                to="/settings"
                class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                role="menuitem"
              >
                <UserCircleIcon class="mr-2.5 h-5 w-5 group-hover:text-spark-purple" />
                Account
              </router-link>
              <router-link
                v-if="!isPartner"
                to="/settings/plan"
                class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                role="menuitem"
              >
                <SwitchVerticalIcon class="mr-2.5 h-5 w-5 group-hover:text-spark-purple" />
                Change Plan
              </router-link>
              <router-link
                to="/help"
                class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                role="menuitem"
              >
                <QuestionMarkCircleIcon class="mr-2.5 h-5 w-5 group-hover:text-spark-purple" />
                Help
              </router-link>
              <a
                class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                href="https://docs.sparklayer.io/whats-new"
                target="_blank"
                rel="noopener noreferrer"
              >
                <BellIcon class="mr-2.5 h-5 w-5 group-hover:text-spark-purple" />
                What's New
              </a>
            </div>
            <div class="py-1">
              <router-link
                to="/logout"
                class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                role="menuitem"
              >
                <LogoutIcon class="mr-2.5 h-5 w-5 group-hover:text-spark-purple" />
                Sign out
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile title -->
    <div class="border-b border-gray-200 bg-white p-4 mt-16 md:px-6 lg:hidden">
      <div class="flex-1 min-w-0">
        <h1 class="text-lg font-medium leading-6 text-gray-900 sm:truncate">
          {{ title }}
        </h1>
      </div>
    </div>
  </header>
</template>

<script>

import {
  ArrowRightIcon,
  BellIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  GlobeAltIcon,
  LogoutIcon,
  MenuAlt1Icon,
  QuestionMarkCircleIcon,
  SwitchVerticalIcon,
  UserCircleIcon,
} from '@heroicons/vue/outline';
import { mapGetters } from 'vuex';

export default {
  components: {
    UserCircleIcon,
    BellIcon,
    SwitchVerticalIcon,
    LogoutIcon,
    QuestionMarkCircleIcon,
    ArrowRightIcon,
    GlobeAltIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    MenuAlt1Icon,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
  },
  emits: [
    'toggleSidebar',
  ],
  data() {
    return {
      userMenuOpen: false,
    };
  },
  computed: {
    ...mapGetters({
      name: 'auth/name',
      acronymName: 'auth/acronymName',
      isAdvancedFeaturesEnabled: 'auth/isAdvancedFeaturesEnabled',
      isPartner: 'auth/isPartner',
      isLive: 'env/isLive',
      team: 'auth/team',
      showCoreNav: 'partner/showCoreNav',
    }),
    showPlanLink() {
      if (this.isPartner) {
        return false;
      }
      const trialEndsAt = new Date(this.team.shopify_recurring_trial_ends_at);
      return !this.team.shopify_enterprise_billing
        && (!this.team.shopify_recurring_plan
          || (this.team.shopify_recurring_plan && trialEndsAt > new Date()));
    },
    planLinkText() {
      if (!this.team.shopify_recurring_plan) {
        return 'Please select a plan';
      }
      const trialEndsAt = new Date(this.team.shopify_recurring_trial_ends_at);
      const now = new Date();
      const daysDiff = Math.round((trialEndsAt - now) / (1000 * 60 * 60 * 24));
      return `${daysDiff} days left of trial`;
    },
  },
};
</script>
